/**
 * 星球活动页 /events（通讯器 · 星球活动，二级页全屏，外面由 WorldGate 守；左上角回通讯器。入口是通讯器里公告细条下面那一行，
 * 见 ./PlanetEventsEntry）。规矩见 ./events 的文件头（CR-WORLD-OPS-PHASE2A 第 0、3 节）：
 * - 每个活动：标题、时间窗、地点（去处的说法，有真实地点名字时带上）、主题故事（运营原文，保留换行）、车费那一句（原文）；
 * - 这只宠物：没告诉过 →“告诉 TA”；告诉了 →“已经告诉 TA 了”＋“不告诉了”；两个按钮旁都写“去不去由 TA 自己决定”；
 *   TA 自己听说的（D10）→“TA 自己听说的”＋“撤回”（撤的是听说那一行）；两样都有 →“家人告诉过（TA 也听说了）”＋“不告诉了”（只撤家人那一行）；
 *   TA 自己去了 →“TA 自己去了”＋去那一趟的链接，不给按钮（撤回改变不了已经发生的事）；
 * - 不告诉了、一直没理会的，样子和没告诉过的一样，不标记、不挪位置、别处也不提醒；作废（活动结束或取消）的不列；
 * - TA 去过、活动已经结束的，列在最后“TA 去过的活动”里，照实记一笔；
 * - 被拒只说人话（活动没开放了、TA 现在去不了那里……），原因码不给玩家看；认不出的出错也只说给玩家的那一句，错误码不上页面（X-09）；
 * - 演示模式挂“演示活动”标识。
 */
import { useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import type { PlanetEvent, PlanetEventTell } from "@/shared/contracts";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { Button, DataOriginBadge, EmptyState, ErrorState, Icon, LoadingState, Page, TopBar } from "@/shared/ui";
import { eventRows, pastTrips, placeText, refusalOf, tripHref, tellNote, windowText, withdrawSourceFor, type Refusal, type TellState } from "./events";
import { useEventPet, useEventTellActions, usePlanetEventList, usePlanetEventTells } from "./usePlanetEvents";
import "./events.css";

const DECIDE = "去不去由 TA 自己决定";

function WentLine({ journeyId }: { journeyId: string | null }) {
  return (
    <p className="ps-event__went">
      <span>TA 自己去了</span>
      {journeyId ? (
        <Link to={tripHref(journeyId)}>
          看这一趟
          <Icon name="chevron" size={14} strokeWidth={2.2} />
        </Link>
      ) : null}
    </p>
  );
}

/** 按钮这一行：告诉 TA / 不告诉了，旁边写“去不去由 TA 自己决定”；被拒的那一句跟在下面。 */
function EventActions({ event, state, journeyId, userId, petId }: { event: PlanetEvent; state: TellState; journeyId: string | null; userId: string | null; petId: string | null }) {
  const { tell, withdraw, refresh } = useEventTellActions(userId, petId);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  // 连点只发一次：按钮的“进行中”要等下一次渲染才挂上，同一瞬间的第二下靠这个同步标记挡住。
  const inFlight = useRef(false);
  if (state === "went") return <WentLine journeyId={journeyId} />;
  // 活动没开放了：告诉不了，按钮收起，只留那一句（下次打开这一页它就不在列表里了）。
  const closed = refusal?.reason === "event_not_open";
  const busy = tell.isPending || withdraw.isPending;
  const withdrawSource = withdrawSourceFor(state);
  const run = (which: "tell" | "withdraw") => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRefusal(null);
    const handlers = {
      onError: (error: unknown) => {
        const next = refusalOf(error);
        setRefusal(next);
        // 状态可能已经变了（TA 已经去了、没告诉过）：重读这只宠物的记录，按钮跟着变。
        if (next.reason === "tell_already_used" || next.reason === "tell_not_found") refresh();
      },
      onSettled: () => {
        inFlight.current = false;
      },
    };
    if (which === "tell") tell.mutate(event.event_id, handlers);
    else withdraw.mutate({ eventId: event.event_id, source: withdrawSource ?? "owner" }, handlers);
  };
  return (
    <div className="ps-event__actions">
      {tellNote(state) ? (
        <p className="ps-event__told">
          <Icon name="check" size={15} strokeWidth={2.2} />
          {tellNote(state)}
        </p>
      ) : null}
      {closed || !petId ? null : (
        <div className="ps-event__row">
          {withdrawSource ? (
            <Button variant="ghost" loading={withdraw.isPending} disabled={busy} onClick={() => run("withdraw")}>
              {withdrawSource === "heard" ? "撤回" : "不告诉了"}
            </Button>
          ) : (
            <Button variant="secondary" loading={tell.isPending} disabled={busy} onClick={() => run("tell")}>
              告诉 TA
            </Button>
          )}
          <span className="ps-event__decide">{DECIDE}</span>
        </div>
      )}
      {refusal ? (
        <div className="ps-event__refusal" role="status">
          <p>{refusal.text}</p>
        </div>
      ) : null}
    </div>
  );
}

/** 每张卡的车费说法都一样时（活动多于一个），在列表上面说一次、卡上不再重复（UI-V-PAGES-01，总账 V-14：一页只说一次）；说法不一样就各写各的 */
function sharedFare(events: readonly PlanetEvent[]): string | null {
  const notes = events.map((event) => event.fare_note.trim());
  return notes.length > 1 && notes[0] && notes.every((note) => note === notes[0]) ? notes[0] : null;
}

function EventCard({ event, state, journeyId, tellsReady, userId, petId, showFare }: { event: PlanetEvent; state: TellState; journeyId: string | null; tellsReady: boolean; userId: string | null; petId: string | null; showFare: boolean }) {
  const titleId = useId();
  const when = windowText(event.window_start, event.window_end);
  return (
    <li>
      <article className="ps-event" aria-labelledby={titleId} data-event={event.event_id} data-state={tellsReady ? state : "unknown"}>
        <h2 id={titleId} className="ps-event__title">
          {event.title}
        </h2>
        <dl className="ps-event__facts">
          {when ? (
            <div>
              <dt>时间</dt>
              <dd>
                <time dateTime={event.window_start}>{when}</time>
              </dd>
            </div>
          ) : null}
          <div>
            <dt>地点</dt>
            <dd>{placeText(event)}</dd>
          </div>
        </dl>
        {event.story.trim() ? <p className="ps-event__story">{event.story}</p> : null}
        {showFare && event.fare_note.trim() ? (
          <p className="ps-event__fare">
            <Icon name="coin" size={14} />
            {event.fare_note}
          </p>
        ) : null}
        {tellsReady ? <EventActions key={petId ?? "-"} event={event} state={state} journeyId={journeyId} userId={userId} petId={petId} /> : null}
      </article>
    </li>
  );
}

function PastTrip({ tell }: { tell: PlanetEventTell }) {
  const when = windowText(tell.window_start, tell.window_end);
  return (
    <li className="ps-event-past" data-event={tell.event_id}>
      <strong>{tell.title}</strong>
      {when ? <small>{when}</small> : null}
      <WentLine journeyId={tell.journey_id} />
    </li>
  );
}

export function PlanetEventsPage() {
  const list = usePlanetEventList();
  const { userId, petId } = useEventPet();
  const tells = usePlanetEventTells(userId, petId);
  const pastId = useId();
  const tellsReady = tells.isSuccess;
  const myTells = tells.data?.tells ?? [];

  let content: ReactNode;
  if (list.isPending) {
    content = <LoadingState lines={2} label="正在取星球活动…" />;
  } else if (list.isError) {
    const error = toApiError(list.error);
    // 后端还没有这条路（或能力没开）：温和说明“暂时不可用”，不当成出错、也不说“没有活动”。
    content =
      error.unknownRoute || error.isCapabilityUnavailable ? (
        <EmptyState icon="info" title="星球活动暂时不可用">
          过一会儿再来看看。
        </EmptyState>
      ) : (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      );
  } else {
    const rows = eventRows(list.data.events, myTells);
    const past = tellsReady ? pastTrips(list.data.events, myTells) : [];
    const fare = sharedFare(rows.map((row) => row.event));
    content = (
      <>
        {tells.isError ? (
          <div className="ps-event-note" role="status">
            <p>暂时读不到告诉过 TA 哪些活动，按钮先不放。</p>
            <Button variant="ghost" size="sm" icon="refresh" onClick={() => void tells.refetch()}>
              再试一次
            </Button>
          </div>
        ) : null}
        {fare ? (
          <p className="ps-event__fare ps-events__fare">
            <Icon name="coin" size={14} />
            {fare}
          </p>
        ) : null}
        {rows.length ? (
          <ul className="ps-events" aria-label="对大家开放的活动">
            {rows.map((row) => (
              <EventCard key={row.event.event_id} event={row.event} state={row.state} journeyId={row.journeyId} tellsReady={tellsReady} userId={userId} petId={petId} showFare={!fare} />
            ))}
          </ul>
        ) : (
          <EmptyState icon="planet" title="现在没有对大家开放的活动">
            有对大家开放的活动时，会列在这里。
          </EmptyState>
        )}
        {past.length ? (
          <section className="ps-events-past" aria-labelledby={pastId}>
            <h2 id={pastId}>TA 去过的活动</h2>
            <ul>
              {past.map((tell) => (
                <PastTrip key={tell.event_id} tell={tell} />
              ))}
            </ul>
          </section>
        ) : null}
      </>
    );
  }

  return (
    <Page bare className="ps-events-page">
      <TopBar
        title="星球活动"
        subtitle="对大家开放的公共活动"
        // 从手机首屏的星球活动入口进来；没有站内上一页时回手机首屏（P2）
        back="/communicator?channel=phone"
        right={env.dataMode === "fixture" ? <DataOriginBadge origin="fixture" label="演示活动" /> : undefined}
      />
      {content}
    </Page>
  );
}
